import { Banner, CloseButton } from '@zillow/constellation-10';
import { yieldCallback } from '@zillow/yield-callback';
import React, { useState } from 'react';

/**
 * A component that displays a warning banner when the user is in preview mode.
 */
const PreviewModeAlert: React.FC = () => {
    const [isOpen, setIsOpen] = useState(true);
    return (
        // The `<Banner>` composite is the 1:1 counterpart of v8's Banner: it
        // takes the copy as `children` (v8's `body` prop) and the dismiss
        // control as `closeButton`, exactly like the v8 call site. It renders
        // the warning icon too — Banner.Body mounts Banner.Icon internally and
        // Banner.Icon reads `tone` off Banner.Root's context, so nothing has to
        // be passed down (warning → IconWarningFilled, the v10 counterpart of
        // v8's IconWarning).
        <Banner
            // v8 `appearance` → v10 `tone`.
            tone="warning"
            isOpen={isOpen}
            // The close handler has to live here, not on CloseButton. v10's
            // Banner.Close clones the child and *overwrites* its `onClick` with
            // its own handler, so an onClick passed to CloseButton would be
            // silently dropped. That internal handler calls `closeBanner()` (a
            // no-op while `isOpen` is supplied, i.e. while the Banner is
            // controlled) and then this `onClose`, which is what actually hides
            // the banner.
            onClose={yieldCallback(() => setIsOpen(false))}
            closeButton={<CloseButton title="Close preview banner" />}
            css={{
                // v8 styled(Banner) block, moved onto the css prop — the
                // styled() factory is not allowed in v10.
                position: 'fixed',
                // 2.5rem is `spacing.1000`, but that raw step has no semantic
                // alias in the v10 scale (which goes `looser` 2rem →
                // `layout.looser` 3rem), so the literal reads clearer than the
                // numeric token here.
                bottom: '2.5rem',
                /* Take full width but center it with 2.5% horizontal margins. */
                left: '2.5%',
                /* 2.5% * 2 is 5% and 100% - 5% is 95%. */
                width: '95%',
                // v8 hardcoded `0 8px 12px 0 rgba(0, 0, 0, 0.3)` with the comment
                // "Copied from Toast". `md` is that exact value on legacy-zillow
                // and is what `Toast` itself resolves to on the visual-refresh
                // theme, so it preserves the shadow on both. (Using the `Toast`
                // token directly would silently lighten it on legacy-zillow,
                // where `Toast` aliases the smaller `sm-heavy`.)
                boxShadow: 'md',
                // Kept as the literal 1000 rather than `zIndex: 'toast'`. The v10
                // zIndex scale starts at 90000 and `toast` is 110000, which is
                // above `modal` (100000) — switching would newly float this banner
                // over modals. 1000 still clears the Zillow header, which is all
                // the original comment asked for.
                zIndex: 1000,
            }}
        >
            You are viewing a preview of this content.
        </Banner>
    );
};

export default PreviewModeAlert;
